import { Button } from '@mui/material'
import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DataTable } from 'components/Shared/DataTable'
import {
  type GridRenderCellParams,
  type GridColDef,
  type GridEventListener,
} from '@mui/x-data-grid'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { dataTablePromotionDateFormatter } from 'utils/util'
import { useLayout } from 'hooks/useLayout'
import { isNil } from 'lodash'
import { type SystemBannerDTO } from 'types/api'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { useGetAllSystemBanners } from 'hooks/api/useGetAllSystemBanners'
import { DeleteBAnnerRenderCell } from './DeleteBanner/DeleteBannerRenderCell'
import { DeleteBannerModal } from './DeleteBanner/DeleteBannerModal'

export const BannersPage = () => {
  const navigate = useNavigate()

  const [bannerRowData, setBannerRowData] = useState<SystemBannerDTO | null>()
  const { isMobile } = useLayout()
  const useGetAllBannersQuery = useGetAllSystemBanners()
  const [filteredData, setFilteredData] = useState<SystemBannerDTO[]>()

  const handleFilteredData = (filteredData: SystemBannerDTO[]) => {
    setFilteredData(filteredData)
  }

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (params.field !== '__menu__' && params.id !== null) {
      navigate(`/Communications/Banners/${params.id}`)
    }
  }

  const getColumns = (isMobile: boolean): GridColDef[] => [
    { field: 'title', headerName: 'Title', minWidth: 225, flex: 1 },
    { field: 'content', headerName: 'Content', minWidth: 145, flex: 1 },
    { field: 'type', headerName: 'Banner Type', minWidth: 145, flex: 1 },
    {
      field: 'platformType',
      headerName: 'Platform Type',
      minWidth: 145,
      flex: 1,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      minWidth: 145,
      flex: 1,
      valueFormatter: dataTablePromotionDateFormatter(isMobile, 'Start'),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      minWidth: 145,
      flex: 1,
      valueFormatter: dataTablePromotionDateFormatter(isMobile, 'End'),
    },

    {
      field: '__menu__',
      headerName: '',
      align: 'left',
      renderCell: (params: GridRenderCellParams<SystemBannerDTO>) => {
        return (
          <DeleteBAnnerRenderCell
            params={params}
            setBannerRowData={setBannerRowData}
          />
        )
      },
    },
  ]

  return (
    <Page
      header={
        <PageHeader
          title="Banners"
          actionButton={
            <Button
              id="message-button"
              variant="contained"
              component={Link}
              onClick={() => navigate('/Communications/SystemBanner/New')}
              to="/Communications/SystemBanner/New"
            >
              Create New Banner
            </Button>
          }
        />
      }
    >
      {useGetAllBannersQuery.isPending && <ActivityIndicator />}
      {useGetAllBannersQuery.isError && <p>An error occured.</p>}
      {!useGetAllBannersQuery.isError && useGetAllBannersQuery.data && (
        <>
          <GridSearchField<'SystemBannerDTO'>
            placeholder="Search Banners"
            data={useGetAllBannersQuery.data ?? []}
            handleFilteredData={handleFilteredData}
            fullWidth
          />
          <DataTable
            getRowHeight={() => 'auto'}
            sx={{
              [`& .MuiDataGrid-cell`]: {
                paddingTop: 1.5,
                paddingBottom: 1.5,
                lineHeight: 'unset !important',
                maxHeight: 'none !important',
                minHeight: '70.87px !important',
                whiteSpace: 'normal',
                color: 'text.primary',
              },
              [`& .MuiDataGrid-columnHeader`]: {
                maxHeight: 'none !important',
                minHeight: '70.87px !important',
                height: 'auto !important',
                whiteSpace: 'inherit !important',
                overflow: 'inherit !important',
                lineHeight: '24px !important',
              },
              [`& .MuiDataGrid-columnHeaderTitle`]: {
                whiteSpace: 'normal !important',
              },
            }}
            data-testid="data-table"
            sort="desc"
            sortFieldName={'deliveryDate'}
            rowHeight={65}
            columns={getColumns(isMobile)}
            rows={filteredData ?? useGetAllBannersQuery.data}
            onCellClick={selectCell}
          />
        </>
      )}

      <DeleteBannerModal
        data={bannerRowData}
        isModalOpen={!isNil(bannerRowData)}
        toggleIsOpen={() => setBannerRowData(undefined)}
      />
    </Page>
  )
}
